import { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../../../config/api';

const POLL_INTERVAL_MS = 2000;
const WS_RETRY_MS = 4000;

const resolveOverlayApiUrl = ({ matchId, tournamentId = '' }) => {
  if (!matchId) return '';
  const base = String(API_BASE || '').trim().replace(/\/$/, '');
  const root = base || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!root) return '';
  const url = new URL(`/api/matches/${encodeURIComponent(matchId)}/overlay`, root);
  if (tournamentId) {
    url.searchParams.set('tournamentId', tournamentId);
  }
  return url.toString();
};

const ObsOverlayPage = () => {
  const searchParams = useMemo(
    () => (typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()),
    []
  );
  const matchId = searchParams.get('matchId') || '';
  const tournamentId = searchParams.get('tournamentId') || '';
  const wsUrl = searchParams.get('wsUrl') || '';

  const [overlay, setOverlay] = useState(null);
  const [error, setError] = useState('');
  const pollTimerRef = useRef(null);
  const wsRef = useRef(null);
  const wsRetryRef = useRef(null);

  const overlayApiUrl = resolveOverlayApiUrl({ matchId, tournamentId });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    const previousHtmlBackground = htmlEl.style.background;
    const previousBodyBackground = bodyEl.style.background;
    htmlEl.style.background = 'transparent';
    bodyEl.style.background = 'transparent';
    return () => {
      htmlEl.style.background = previousHtmlBackground;
      bodyEl.style.background = previousBodyBackground;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!overlayApiUrl) {
      setError('Missing matchId in query string.');
      return undefined;
    }

    const fetchOverlay = async () => {
      try {
        const response = await fetch(overlayApiUrl, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Overlay API failed with status ${response.status}`);
        }
        const payload = await response.json();
        if (!cancelled) {
          setOverlay(payload);
          setError('');
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError?.message || 'Unable to fetch overlay data.');
        }
      }
    };

    const startPolling = () => {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(fetchOverlay, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (!pollTimerRef.current) return;
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };

    const setupWebSocket = () => {
      if (!wsUrl || typeof WebSocket === 'undefined') {
        startPolling();
        return;
      }

      try {
        wsRef.current = new WebSocket(wsUrl);
      } catch (_error) {
        startPolling();
        return;
      }

      wsRef.current.onopen = () => {
        stopPolling();
      };
      wsRef.current.onmessage = (event) => {
        try {
          const payload = JSON.parse(event?.data || '{}');
          setOverlay(payload);
          setError('');
        } catch (_error) {
          // ignore non-json payloads, polling still keeps state fresh
        }
      };
      wsRef.current.onerror = () => {
        startPolling();
      };
      wsRef.current.onclose = () => {
        startPolling();
        wsRetryRef.current = setTimeout(setupWebSocket, WS_RETRY_MS);
      };
    };

    fetchOverlay();
    startPolling();
    setupWebSocket();

    return () => {
      cancelled = true;
      stopPolling();
      if (wsRetryRef.current) {
        clearTimeout(wsRetryRef.current);
        wsRetryRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [overlayApiUrl, wsUrl]);

  return (
    <div style={styles.stage}>
      <div style={styles.canvas}>
        {!matchId ? (
          <div style={styles.notice}>Add <code>?matchId=&lt;matchId&gt;</code> to the URL.</div>
        ) : null}
        {error && matchId ? <div style={styles.notice}>{error}</div> : null}
        {overlay ? (
          <div style={styles.scorebar}>
            <div style={styles.team}>{overlay.battingTeamAbbreviation || overlay.battingTeamName || 'TEAM A'}</div>
            <div style={styles.score}>
              {overlay.runs ?? 0}/{overlay.wickets ?? 0}
            </div>
            <div style={styles.overs}>OV {overlay.overs || '0.0'}</div>
            <div style={styles.meta}>{overlay.strikerName || 'Striker'} {overlay.strikerRunsBalls ? `(${overlay.strikerRunsBalls})` : ''}</div>
            <div style={styles.meta}>{overlay.nonStrikerName || 'Non-striker'} {overlay.nonStrikerRunsBalls ? `(${overlay.nonStrikerRunsBalls})` : ''}</div>
            <div style={styles.meta}>BWL {overlay.bowlerName || '-'} {overlay.bowlerFigures ? `(${overlay.bowlerFigures})` : ''}</div>
            <div style={styles.meta}>CRR {overlay.crr || '0.00'}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const styles = {
  stage: {
    width: '100vw',
    height: '100vh',
    background: 'transparent',
    overflow: 'hidden',
    margin: 0,
    padding: 0,
  },
  canvas: {
    width: '1920px',
    height: '1080px',
    transformOrigin: 'top left',
    transform: 'scale(min(calc(100vw / 1920), calc(100vh / 1080)))',
    background: 'transparent',
    position: 'relative',
  },
  scorebar: {
    position: 'absolute',
    left: 42,
    bottom: 42,
    minWidth: 760,
    background: 'rgba(8, 15, 31, 0.72)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 14,
    color: '#fff',
    padding: '16px 20px',
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    columnGap: 16,
    rowGap: 6,
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  },
  team: { fontSize: 20, fontWeight: 700, alignSelf: 'end' },
  score: { fontSize: 36, fontWeight: 800, lineHeight: 1 },
  overs: { fontSize: 18, fontWeight: 700, alignSelf: 'center' },
  meta: { gridColumn: '1 / span 3', fontSize: 15, opacity: 0.94 },
  notice: {
    position: 'absolute',
    left: 24,
    top: 24,
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 8,
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
  },
};

export default ObsOverlayPage;
